import { useState, useEffect, useContext, useRef } from 'react';
import { TasksService } from '../../../../../services/openapi/services/TasksService';
import { UserService } from '../../../../../services/openapi/services/UserService';
import { ArbeitszeitService } from '../../../../../services/openapi/services/ArbeitszeitService';
import type { Task } from '../../../../../services/openapi/models/Task';
import type { TaskImage } from '../../../../../services/openapi/models/TaskImage';
import type { TaskDocument } from '../../../../../services/openapi/models/TaskDocument';
import type { TaskUpdate } from '../../../../../services/openapi/models/TaskUpdate';
import type { TaskChangeLog } from '../../../../../services/openapi/models/TaskChangeLog';
import type { BauvorhabenWithID } from '../../../../../services/openapi/models/BauvorhabenWithID';
import type { Contact } from '../../../../../services/openapi/models/Contact';
import { IKunde } from '../../Hausverwaltung/interfaces';
import { TaskStatus } from '../../../../../services/openapi/models/TaskStatus';
import UserContext from '../../../../../Contexts/UserContext/UserContext';
import APIWrapperContext from '../../../../../services/APIWrapperContext';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import dayjs, { Dayjs } from 'dayjs';

export interface ChangelogEntry {
  id?: number;
  task_id?: number;
  task_title?: string;
  changed_by?: string;
  changed_by_name?: string;
  event_type?: string;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  change_details?: string;
  changed_at?: string;
  timestamp?: string;
  user_name?: string;
  user_id?: string;
  action?: string;
  details?: string;
}

export interface TaskWithImages extends Task {
  images?: TaskImage[];
  documents?: TaskDocument[];
  rapport?: string; // Explicitly include rapport to ensure TypeScript recognition
  // Projekt name for free text input
  projekt_name?: string;
  // Extended Auftraggeber fields
  auftraggeber_street?: string;
  auftraggeber_additional_street?: string;
  auftraggeber_postal_code?: string;
  auftraggeber_city?: string;
  // Terminvereinbarung (appointment contact) fields
  appointment_name?: string;
  appointment_email?: string;
  appointment_phone?: string;
  appointment_street?: string;
  appointment_additional_street?: string;
  appointment_postal_code?: string;
  appointment_city?: string;
  // Rechnungsempfänger (invoice recipient) fields
  invoice_name?: string;
  invoice_email?: string;
  invoice_phone?: string;
  invoice_street?: string;
  invoice_additional_street?: string;
  invoice_postal_code?: string;
  invoice_city?: string;
}

export const useTaskDetail = (taskId: number, isOpen: boolean, onTaskUpdated?: () => void) => {
  const [task, setTask] = useState<TaskWithImages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<IKunde[]>([]);
  const [bau, setBau] = useState<BauvorhabenWithID[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState<TaskImage[]>([]);
  const [documents, setDocuments] = useState<TaskDocument[]>([]);
  const [changelog, setChangelog] = useState<ChangelogEntry[]>([]);
  const [assignedUser, setAssignedUser] = useState<IKunde | null>(null);
  const [bauvorhaben, setBauvorhaben] = useState<BauvorhabenWithID | null>(null);
  const [duration, setDuration] = useState<string>('');

  const { call_backend_api } = useContext(APIWrapperContext);
  const { user_roll, email: currentUserEmail } = useContext(UserContext);
  const sessionContext = useSessionContext();
  const currentUserId = (sessionContext as any).userId;
  const sessionUserId = (sessionContext as any).userId;
  const isAdmin = user_roll?.includes('Administrator');

  const isTaskArchived = task?.status === TaskStatus.ARCHIVED;
  const isTaskCreator = task?.created_by === sessionUserId;

  const [wtFrom, setWtFrom] = useState<Dayjs | null>(null);
  const [wtTo, setWtTo] = useState<Dayjs | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadTask = async () => {
    if (!isOpen || !taskId) return;
    
    setLoading(true);
    setError(null);

    Promise.all([
      TasksService.getTaskTaskTaskIdGet(taskId),
      TasksService.getTaskImagesTaskTaskIdImagesGet(taskId),
      TasksService.getTaskDocumentsTaskTaskIdDocumentsGet(taskId).catch(() => []),
      isAdmin ? UserService.listUsersWithMetadataUserListWithMetadataGet() : Promise.resolve([]),
      ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false),
      TasksService.getAllContactsTaskContactsAllGet(),
    ])
      .then(([t, imgs, docs, us, bv, contactsData]) => {
        // Ensure rapport_allow has a default value if not provided by backend
        const taskWithDefaults = {
          ...t,
          rapport_allow: t.rapport_allow ?? false,
          images: imgs,
          documents: docs || []
        };
        setTask(taskWithDefaults);
        setImages(imgs);
        setDocuments(docs || []);
        // Filter users to only show Administrators and Aufgaben Mitarbeiter
        const filteredUsers = (us as IKunde[]).filter((u: IKunde) =>
          u.userclass.includes('Administrator') || u.userclass.includes('Aufgaben Mitarbeiter')
        );
        setUsers(filteredUsers);
        setBau(bv as unknown as BauvorhabenWithID[]);
        setContacts(contactsData as Contact[]);
        setWtFrom(t.time_from ? dayjs(t.time_from) : null);
        setWtTo(t.time_to ? dayjs(t.time_to) : null);
        setLoading(false);
      })
      .catch((e) => {
        console.error(e);
        setError('Laden fehlgeschlagen');
        setLoading(false);
      });
  };

  // Load users
  const loadUsers = async () => {
    if (!isAdmin) {
      setUsers([]);
      return;
    }

    call_backend_api(
      UserService.listUsersWithMetadataUserListWithMetadataGet(),
      (data: IKunde[]) => {
        setUsers(data.filter((u: IKunde) => u.userclass.includes('Administrator') || u.userclass.includes('Aufgaben Mitarbeiter')));
      },
      (apiError: any) => {
        console.error('Fehler beim Laden der Benutzer:', apiError);
      },
      undefined,
      false
    );
  };

  const loadChangelog = async () => {
    if (!taskId) return;

    call_backend_api(
      TasksService.getTaskChangelogTaskTaskIdChangelogGet(taskId),
      (data: ChangelogEntry[]) => {
        setChangelog(data);
      },
      (apiError: any) => {
        console.error('Fehler beim Laden des Changelogs:', apiError);
      },
      undefined,
      false
    );
  };

  const getCurrentTimeFrom = () => wtFrom || (task?.time_from ? dayjs(task.time_from) : null);
  const getCurrentTimeTo = () => wtTo || (task?.time_to ? dayjs(task.time_to) : null);

  const getTotalDuration = () => {
    const currentFrom = getCurrentTimeFrom();
    const currentTo = getCurrentTimeTo();
    if (!currentFrom || !currentTo) return null;
    
    const diffMs = currentTo.diff(currentFrom);
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const percentOfDay = Math.min((totalMinutes / (8 * 60)) * 100, 100);
    
    return { hours, minutes, percentOfDay };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    if (isOpen && taskId) {
      loadTask();
      loadUsers();
      loadChangelog();
    }
  }, [isOpen, taskId]);

  return {
    task,
    setTask,
    loading,
    error,
    users,
    bau,
    setBau,
    contacts,
    uploading,
    setUploading,
    images,
    setImages,
    documents,
    setDocuments,
    changelog,
    assignedUser,
    setAssignedUser,
    bauvorhaben,
    setBauvorhaben,
    duration,
    setDuration,
    wtFrom,
    setWtFrom,
    wtTo,
    setWtTo,
    fileInputRef,
    
    currentUserId,
    sessionUserId,
    isAdmin,
    isTaskArchived,
    isTaskCreator,
    
    loadTask,
    loadUsers,
    loadChangelog,
    getCurrentTimeFrom,
    getCurrentTimeTo,
    getTotalDuration,
    formatDate,
  };
};
